import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, Send, CheckCircle2, AlertCircle, Briefcase, Heart, ShieldCheck,
  Clock3, Award, Users, Upload, FileText, ArrowRight, Phone, ChevronLeft,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import StickyCall from "@/components/StickyCall";
import Seo, { LOCAL_BUSINESS_SCHEMA } from "@/components/Seo";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { createDriverApplication } from "@/lib/api";
import { COMPANY } from "@/lib/data";

const initial = {
  full_name: "", phone: "", email: "", home_address: "", city_state: "",
  at_least_21: null,
  has_valid_license: null, license_number: "", license_state: "", years_driving: "",
  moving_violations_3yrs: null, moving_violations_details: "",
  dui_dwi: null, dui_dwi_details: "",
  currently_employed: null, current_employer: "",
  transport_experience: null, senior_disability_experience: null, relevant_experience: "",
  has_certifications: null, certifications_list: "",
  position_type: "", days_available: "", hours_available: "",
  can_assist_passengers: null, can_operate_wheelchairs: null, can_lift_equipment: null,
  willing_background_check: null, willing_mvr_check: null, willing_drug_screen: null,
  interest_reason: "", additional_comments: "",
  consent: false,
  resume_base64: null, resume_filename: null,
};

const REQUIRED_TEXT = {
  full_name: "Full name is required",
  phone: "Phone is required",
  email: "Email is required",
  home_address: "Home address is required",
  city_state: "City and state are required",
  license_number: "Driver's license number is required",
  license_state: "State of issue is required",
  years_driving: "Years of driving experience is required",
  position_type: "Please choose a position type",
  days_available: "Please list days you are available",
  hours_available: "Please list hours you are available",
  interest_reason: "Please tell us why you want to work with us",
};
const REQUIRED_BOOL = [
  "at_least_21", "has_valid_license",
  "moving_violations_3yrs", "dui_dwi",
  "currently_employed", "transport_experience", "senior_disability_experience",
  "has_certifications",
  "can_assist_passengers", "can_operate_wheelchairs", "can_lift_equipment",
  "willing_background_check", "willing_mvr_check", "willing_drug_screen",
];

const REQUIREMENTS = [
  "Valid driver's license & clean driving record",
  "Pass motor vehicle record (MVR) review",
  "Pass criminal background check",
  "Pass drug screening (when required)",
  "Reliable transportation to and from work",
  "Professional, compassionate, dependable",
  "Excellent customer service skills",
  "Assist passengers entering/exiting vehicles",
  "Safely handle wheelchairs & mobility equipment",
  "Senior / disability experience preferred",
  "CPR / First Aid Certification preferred",
];

const PERKS = [
  { icon: Heart, title: "Meaningful Work", body: "Every ride changes a life — patients depend on you to make their appointments." },
  { icon: ShieldCheck, title: "Safe Equipment", body: "Modern, ADA-compliant wheelchair vans maintained to the highest standard." },
  { icon: Clock3, title: "Flexible Scheduling", body: "Full-time, part-time, and PRN positions available." },
  { icon: Award, title: "Paid Training", body: "Comprehensive onboarding including wheelchair-securement training." },
  { icon: Users, title: "Supportive Team", body: "Work with a compassionate team across VA, DC, and Maryland." },
  { icon: Briefcase, title: "Growing Company", body: "WMATC-certified tri-state operator with steady, growing demand." },
];

export default function CareersDriver() {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const refs = useRef({});

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const next = {};
    for (const [k, msg] of Object.entries(REQUIRED_TEXT)) {
      if (!form[k] || !String(form[k]).trim()) next[k] = msg;
    }
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = "Please enter a valid email";
    if (form.phone && form.phone.replace(/\D/g, "").length < 10) next.phone = "Please enter a 10-digit phone number";

    for (const k of REQUIRED_BOOL) {
      if (form[k] !== true && form[k] !== false) next[k] = "Please select Yes or No";
    }
    if (form.at_least_21 === false) next.at_least_21 = "You must be at least 21 years old.";
    if (form.has_valid_license === false) next.has_valid_license = "A valid driver's license is required.";

    if (!form.consent) next.consent = "Please confirm the consent statement to submit.";

    setErrors(next);
    return next;
  };

  const handleResume = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Resume must be smaller than 5 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const base64 = String(dataUrl).split(",")[1] || "";
      setForm((f) => ({ ...f, resume_base64: base64, resume_filename: file.name }));
    };
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    const next = validate();
    if (Object.keys(next).length > 0) {
      const firstKey = Object.keys(next)[0];
      const node = refs.current[firstKey];
      if (node && node.scrollIntoView) {
        node.scrollIntoView({ behavior: "smooth", block: "center" });
        setTimeout(() => node.focus?.(), 350);
      }
      toast.error(`Please complete ${Object.keys(next).length} required field${Object.keys(next).length > 1 ? "s" : ""}.`);
      return;
    }
    setLoading(true);
    try {
      await createDriverApplication(form);
      toast.success("Application submitted! We'll be in touch soon.");
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="NEMT Driver Job Application — Virginia, DC & Maryland | Comfort Care"
        description="Apply now to join Comfort Care Transportation as a Non-Emergency Medical Transportation (NEMT) Driver. Full-time, part-time, PRN positions across Virginia, DC & Maryland."
        path="/careers/driver"
        keywords={["NEMT driver jobs", "wheelchair van driver Virginia", "medical transportation driver DC", "NEMT driver Maryland", "Comfort Care careers"]}
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            LOCAL_BUSINESS_SCHEMA,
            {
              "@type": "JobPosting",
              title: "Non-Emergency Medical Transportation (NEMT) Driver",
              description: "Drive ADA-compliant wheelchair vans transporting patients to medical appointments across Virginia, Washington DC, and Maryland.",
              datePosted: new Date().toISOString().slice(0, 10),
              employmentType: ["FULL_TIME", "PART_TIME", "PER_DIEM"],
              hiringOrganization: {
                "@type": "Organization",
                name: "Comfort Care Transportation",
                sameAs: "https://comfortcarenemt.com",
                logo: COMPANY.logoUrl,
              },
              jobLocation: {
                "@type": "Place",
                address: {
                  "@type": "PostalAddress",
                  streetAddress: "4209 Evergreen Lane, Suite #202",
                  addressLocality: "Annandale",
                  addressRegion: "VA",
                  postalCode: "22003",
                  addressCountry: "US",
                },
              },
              applicantLocationRequirements: [
                { "@type": "State", name: "Virginia" },
                { "@type": "AdministrativeArea", name: "Washington, D.C." },
                { "@type": "State", name: "Maryland" },
              ],
              validThrough: new Date(Date.now() + 90 * 86400000).toISOString().slice(0, 10),
              directApply: true,
            },
          ],
        }}
      />
      <Header />
      <main className="pt-24">
        <section className="grain-bg py-16 md:py-24">
          <div className="container-pad mx-auto max-w-7xl">
            <Link to="/careers" data-testid="back-to-careers" className="inline-flex items-center gap-1 text-sm text-navy/60 hover:text-teal-600">
              <ChevronLeft className="w-4 h-4" /> All open positions
            </Link>
            <div className="grid lg:grid-cols-12 gap-12 mt-6 items-center">
              <div className="lg:col-span-7">
                <span className="eyebrow">
                  <Briefcase className="w-3.5 h-3.5" /> Position 1 of 2 · Now Hiring
                </span>
                <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light text-navy tracking-tight mt-5 leading-[1.05]">
                  NEMT <strong className="font-bold text-teal-600">Driver.</strong>
                </h1>
                <p className="mt-6 text-lg text-navy/70 leading-relaxed max-w-2xl font-light">
                  Join Comfort Care Transportation as a Non-Emergency Medical Transportation (NEMT) Driver.
                  Make a real difference for patients across Virginia, Washington DC, and Maryland — with safe,
                  modern vehicles, paid training, and flexible schedules.
                </p>
                <div className="flex flex-wrap gap-3 mt-8">
                  <a href="#apply" className="btn-primary" data-testid="driver-apply-cta">Apply Now <ArrowRight className="w-4 h-4" /></a>
                  <a href={`tel:${COMPANY.phoneRaw}`} className="btn-outline">
                    <Phone className="w-4 h-4" /> Call {COMPANY.phone}
                  </a>
                </div>
                <div className="mt-8 flex flex-wrap gap-4 text-sm text-navy/70">
                  <Tag>Full-Time</Tag>
                  <Tag>Part-Time</Tag>
                  <Tag>PRN / As Needed</Tag>
                </div>
              </div>
              <div className="lg:col-span-5">
                <img
                  src="https://customer-assets.emergentagent.com/job_comfort-care-booking/artifacts/l5bnk78a_2C3E0AE7-BC6B-41D5-8937-5C064B8E8692.PNG"
                  alt="Comfort Care Transportation branded wheelchair van"
                  className="rounded-3xl w-full h-[420px] object-cover shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="section-pad">
          <div className="container-pad mx-auto max-w-7xl">
            <div className="text-center max-w-3xl mx-auto mb-14">
              <span className="eyebrow justify-center">
                <span className="w-8 h-px bg-teal-500" />
                Why Drive with Comfort Care
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-light text-navy tracking-tight mt-5 leading-[1.15]">
                A job with <strong className="font-bold text-teal-600">real impact.</strong>
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-100 rounded-3xl overflow-hidden border border-gray-100">
              {PERKS.map((p) => (
                <div key={p.title} className="bg-white p-8 hover:bg-sand transition-colors">
                  <div className="w-12 h-12 rounded-xl bg-teal-500/10 flex items-center justify-center text-teal-600">
                    <p.icon className="w-6 h-6" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-navy mt-5">{p.title}</h3>
                  <p className="text-navy/65 mt-2 leading-relaxed text-sm">{p.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="section-pad bg-sand">
          <div className="container-pad mx-auto max-w-5xl">
            <span className="eyebrow">
              <span className="w-8 h-px bg-teal-500" />
              Job Requirements
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-light text-navy tracking-tight mt-5 leading-[1.15]">
              What we&apos;re <strong className="font-bold text-teal-600">looking for.</strong>
            </h2>
            <div className="grid sm:grid-cols-2 gap-3 mt-8">
              {REQUIREMENTS.map((r) => (
                <div key={r} className="flex items-start gap-3 bg-white p-4 rounded-xl border border-gray-100">
                  <CheckCircle2 className="w-5 h-5 text-teal-500 mt-0.5 shrink-0" />
                  <span className="text-navy/85 text-sm">{r}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="apply" className="section-pad">
          <div className="container-pad mx-auto max-w-4xl">
            {submitted ? (
              <SuccessCard />
            ) : (
              <form
                onSubmit={submit}
                noValidate
                data-testid="driver-application-form"
                className="bg-white rounded-3xl p-7 md:p-10 lg:p-12 border border-gray-100 shadow-[0_30px_80px_-30px_rgba(10,25,47,0.2)]"
              >
                <div className="text-center mb-8 pb-6 border-b border-gray-100">
                  <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy">Driver Application</h2>
                  <p className="text-navy/65 mt-3">Fields marked with <span className="text-red-500 font-semibold">*</span> are required. Takes ~5 minutes.</p>
                </div>

                <FormErrorBanner errors={errors} />

                <Section title="Personal Information">
                  <div className="grid md:grid-cols-2 gap-5">
                    <Field label="Full Name" required error={errors.full_name}>
                      <Input ref={(el) => (refs.current.full_name = el)} value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className={inputCls(errors.full_name)} />
                    </Field>
                    <Field label="Phone Number" required error={errors.phone}>
                      <Input ref={(el) => (refs.current.phone = el)} type="tel" inputMode="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls(errors.phone)} />
                    </Field>
                    <Field label="Email Address" required error={errors.email}>
                      <Input ref={(el) => (refs.current.email = el)} type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls(errors.email)} />
                    </Field>
                    <Field label="City / State" required error={errors.city_state}>
                      <Input ref={(el) => (refs.current.city_state = el)} value={form.city_state} onChange={(e) => set("city_state", e.target.value)} placeholder="Annandale, VA" className={inputCls(errors.city_state)} />
                    </Field>
                    <Field label="Home Address" required error={errors.home_address} full>
                      <Input ref={(el) => (refs.current.home_address = el)} value={form.home_address} onChange={(e) => set("home_address", e.target.value)} placeholder="Street address" className={inputCls(errors.home_address)} />
                    </Field>
                  </div>
                  <YesNo label="Are you at least 21 years old?" value={form.at_least_21} onChange={(v) => set("at_least_21", v)} error={errors.at_least_21} refSet={(el) => (refs.current.at_least_21 = el)} required />
                </Section>

                <Section title="Driver Qualifications">
                  <YesNo label="Do you have a valid driver's license?" value={form.has_valid_license} onChange={(v) => set("has_valid_license", v)} error={errors.has_valid_license} refSet={(el) => (refs.current.has_valid_license = el)} required />
                  <div className="grid md:grid-cols-3 gap-5 mt-5">
                    <Field label="Driver's License #" required error={errors.license_number}>
                      <Input ref={(el) => (refs.current.license_number = el)} value={form.license_number} onChange={(e) => set("license_number", e.target.value)} className={inputCls(errors.license_number)} />
                    </Field>
                    <Field label="State of Issue" required error={errors.license_state}>
                      <Input ref={(el) => (refs.current.license_state = el)} value={form.license_state} onChange={(e) => set("license_state", e.target.value)} placeholder="VA" className={inputCls(errors.license_state)} />
                    </Field>
                    <Field label="Years of Driving Experience" required error={errors.years_driving}>
                      <Input ref={(el) => (refs.current.years_driving = el)} value={form.years_driving} onChange={(e) => set("years_driving", e.target.value)} placeholder="e.g. 5" className={inputCls(errors.years_driving)} />
                    </Field>
                  </div>
                  <YesNo label="Any moving violations in the last 3 years?" value={form.moving_violations_3yrs} onChange={(v) => set("moving_violations_3yrs", v)} error={errors.moving_violations_3yrs} refSet={(el) => (refs.current.moving_violations_3yrs = el)} required />
                  {form.moving_violations_3yrs && (
                    <Field label="Please describe">
                      <Textarea value={form.moving_violations_details} onChange={(e) => set("moving_violations_details", e.target.value)} rows={2} className="text-base rounded-xl" />
                    </Field>
                  )}
                  <YesNo label="Have you ever had a DUI/DWI?" value={form.dui_dwi} onChange={(v) => set("dui_dwi", v)} error={errors.dui_dwi} refSet={(el) => (refs.current.dui_dwi = el)} required />
                  {form.dui_dwi && (
                    <Field label="Please describe">
                      <Textarea value={form.dui_dwi_details} onChange={(e) => set("dui_dwi_details", e.target.value)} rows={2} className="text-base rounded-xl" />
                    </Field>
                  )}
                </Section>

                <Section title="Employment & Experience">
                  <YesNo label="Are you currently employed?" value={form.currently_employed} onChange={(v) => set("currently_employed", v)} error={errors.currently_employed} refSet={(el) => (refs.current.currently_employed = el)} required />
                  {form.currently_employed && (
                    <Field label="Current Employer (optional)">
                      <Input value={form.current_employer} onChange={(e) => set("current_employer", e.target.value)} className="h-12 text-base" />
                    </Field>
                  )}
                  <YesNo label="Do you have previous transportation experience?" value={form.transport_experience} onChange={(v) => set("transport_experience", v)} error={errors.transport_experience} refSet={(el) => (refs.current.transport_experience = el)} required />
                  <YesNo label="Experience with seniors, individuals with disabilities, or healthcare clients?" value={form.senior_disability_experience} onChange={(v) => set("senior_disability_experience", v)} error={errors.senior_disability_experience} refSet={(el) => (refs.current.senior_disability_experience = el)} required />
                  <Field label="Please describe any relevant experience">
                    <Textarea value={form.relevant_experience} onChange={(e) => set("relevant_experience", e.target.value)} rows={3} className="text-base rounded-xl" />
                  </Field>
                </Section>

                <Section title="Certifications">
                  <YesNo label="Do you hold any certifications? (CPR, First Aid, CNA, EMT, PCA, etc.)" value={form.has_certifications} onChange={(v) => set("has_certifications", v)} error={errors.has_certifications} refSet={(el) => (refs.current.has_certifications = el)} required />
                  {form.has_certifications && (
                    <Field label="Please list them">
                      <Input value={form.certifications_list} onChange={(e) => set("certifications_list", e.target.value)} placeholder="CPR/First Aid, CNA, etc." className="h-12 text-base" />
                    </Field>
                  )}
                </Section>

                <Section title="Availability">
                  <Field label="Position type" required error={errors.position_type}>
                    <Select value={form.position_type} onValueChange={(v) => set("position_type", v)}>
                      <SelectTrigger ref={(el) => (refs.current.position_type = el)} className={`h-12 text-base ${errors.position_type ? "border-red-400" : ""}`}>
                        <SelectValue placeholder="Choose a position type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="full_time" className="text-base py-2.5">Full-Time</SelectItem>
                        <SelectItem value="part_time" className="text-base py-2.5">Part-Time</SelectItem>
                        <SelectItem value="prn" className="text-base py-2.5">PRN / As Needed</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <div className="grid md:grid-cols-2 gap-5 mt-5">
                    <Field label="What days are you available?" required error={errors.days_available}>
                      <Input ref={(el) => (refs.current.days_available = el)} value={form.days_available} onChange={(e) => set("days_available", e.target.value)} placeholder="Mon-Fri, weekends, etc." className={inputCls(errors.days_available)} />
                    </Field>
                    <Field label="What hours are you available?" required error={errors.hours_available}>
                      <Input ref={(el) => (refs.current.hours_available = el)} value={form.hours_available} onChange={(e) => set("hours_available", e.target.value)} placeholder="e.g. 6am-3pm" className={inputCls(errors.hours_available)} />
                    </Field>
                  </div>
                </Section>

                <Section title="Physical Requirements">
                  <YesNo label="Are you able to assist passengers entering and exiting vehicles?" value={form.can_assist_passengers} onChange={(v) => set("can_assist_passengers", v)} error={errors.can_assist_passengers} refSet={(el) => (refs.current.can_assist_passengers = el)} required />
                  <YesNo label="Can you safely operate and secure wheelchairs and mobility equipment?" value={form.can_operate_wheelchairs} onChange={(v) => set("can_operate_wheelchairs", v)} error={errors.can_operate_wheelchairs} refSet={(el) => (refs.current.can_operate_wheelchairs = el)} required />
                  <YesNo label="Can you lift and maneuver mobility equipment when necessary?" value={form.can_lift_equipment} onChange={(v) => set("can_lift_equipment", v)} error={errors.can_lift_equipment} refSet={(el) => (refs.current.can_lift_equipment = el)} required />
                </Section>

                <Section title="Screening Requirements">
                  <YesNo label="Are you willing to undergo a criminal background check?" value={form.willing_background_check} onChange={(v) => set("willing_background_check", v)} error={errors.willing_background_check} refSet={(el) => (refs.current.willing_background_check = el)} required />
                  <YesNo label="Are you willing to undergo a motor vehicle record (MVR) review?" value={form.willing_mvr_check} onChange={(v) => set("willing_mvr_check", v)} error={errors.willing_mvr_check} refSet={(el) => (refs.current.willing_mvr_check = el)} required />
                  <YesNo label="Are you willing to undergo drug screening if required?" value={form.willing_drug_screen} onChange={(v) => set("willing_drug_screen", v)} error={errors.willing_drug_screen} refSet={(el) => (refs.current.willing_drug_screen = el)} required />
                </Section>

                <Section title="Additional Information">
                  <Field label="Why are you interested in working with Comfort Care Transportation?" required error={errors.interest_reason}>
                    <Textarea ref={(el) => (refs.current.interest_reason = el)} value={form.interest_reason} onChange={(e) => set("interest_reason", e.target.value)} rows={4} className={`text-base rounded-xl ${errors.interest_reason ? "border-red-400 bg-red-50/40" : ""}`} />
                  </Field>

                  <Field label="Upload Resume (optional, PDF/DOC, max 5 MB)">
                    <label className="flex items-center gap-3 p-4 border-2 border-dashed border-gray-200 hover:border-teal-400 rounded-xl cursor-pointer transition bg-sand/40">
                      <Upload className="w-5 h-5 text-teal-500" />
                      <span className="text-navy/70 text-sm">
                        {form.resume_filename || "Click to upload your resume"}
                      </span>
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        className="hidden"
                        onChange={(e) => handleResume(e.target.files?.[0])}
                      />
                    </label>
                    {form.resume_filename && (
                      <div className="flex items-center gap-2 mt-2 text-sm text-teal-700 bg-teal-500/10 px-3 py-2 rounded-lg">
                        <FileText className="w-4 h-4" />
                        {form.resume_filename}
                        <button type="button" onClick={() => set("resume_base64", null) || set("resume_filename", null)} className="ml-auto text-navy/50 hover:text-red-600 text-xs">Remove</button>
                      </div>
                    )}
                  </Field>

                  <Field label="Additional Comments">
                    <Textarea value={form.additional_comments} onChange={(e) => set("additional_comments", e.target.value)} rows={3} className="text-base rounded-xl" />
                  </Field>
                </Section>

                <div className={`mt-8 p-5 rounded-xl border ${errors.consent ? "border-red-300 bg-red-50/50" : "border-gray-200 bg-sand"}`}>
                  <div className="flex items-start gap-3">
                    <Checkbox id="consent" ref={(el) => (refs.current.consent = el)} checked={form.consent} onCheckedChange={(v) => set("consent", !!v)} className="mt-1" />
                    <label htmlFor="consent" className="text-sm text-navy/85 leading-relaxed cursor-pointer">
                      <strong className="text-navy">Consent Statement.</strong> By submitting this application, I certify that the information provided is accurate and complete.
                      I understand that employment may be contingent upon successful completion of required screenings, including background checks,
                      motor vehicle record reviews, and drug testing as applicable.
                    </label>
                  </div>
                  {errors.consent && (
                    <div className="text-red-600 text-sm mt-2 flex items-center gap-1.5">
                      <AlertCircle className="w-3.5 h-3.5" /> {errors.consent}
                    </div>
                  )}
                </div>

                <button type="submit" data-testid="driver-submit-btn" disabled={loading} className="btn-primary mt-8 w-full !h-14 !text-base disabled:opacity-60">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                  {loading ? "Submitting..." : "Submit Application"}
                </button>
              </form>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <StickyCall />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-8 pt-8 first:mt-0 first:pt-0 first:border-0 border-t border-gray-100">
      <h3 className="font-heading text-lg font-semibold text-navy mb-5 flex items-center gap-2">
        <span className="w-1 h-5 bg-teal-500 rounded-full" />
        {title}
      </h3>
      {children}
    </div>
  );
}

function Field({ label, required, error, children, full }) {
  return (
    <div className={`mt-4 first:mt-0 ${full ? "md:col-span-2" : ""}`}>
      <Label className="text-navy font-semibold text-sm flex items-center gap-1">
        {label}
        {required && <span className="text-red-500" aria-label="required">*</span>}
      </Label>
      <div className="mt-2">{children}</div>
      {error && (
        <div className="flex items-center gap-1.5 mt-1.5 text-red-600 text-sm font-medium">
          <AlertCircle className="w-3.5 h-3.5 shrink-0" /> {error}
        </div>
      )}
    </div>
  );
}

function YesNo({ label, value, onChange, error, refSet, required }) {
  return (
    <div className="mt-5" ref={refSet}>
      <Label className="text-navy font-semibold text-sm flex items-center gap-1">
        {label}
        {required && <span className="text-red-500">*</span>}
      </Label>
      <RadioGroup
        value={value === true ? "yes" : value === false ? "no" : ""}
        onValueChange={(v) => onChange(v === "yes")}
        className="grid grid-cols-2 gap-3 mt-2"
      >
        <YNOption value="yes" current={value === true} label="Yes" />
        <YNOption value="no" current={value === false} label="No" />
      </RadioGroup>
      {error && (
        <div className="flex items-center gap-1.5 mt-1.5 text-red-600 text-sm font-medium">
          <AlertCircle className="w-3.5 h-3.5 shrink-0" /> {error}
        </div>
      )}
    </div>
  );
}

function YNOption({ value, current, label }) {
  return (
    <label
      className={`flex items-center justify-center gap-3 px-4 py-3 rounded-xl border cursor-pointer transition font-medium text-base ${
        current ? "border-teal-500 bg-teal-500/10 text-teal-700" : "border-gray-200 hover:border-teal-300 text-navy/80"
      }`}
    >
      <RadioGroupItem value={value} />
      {label}
    </label>
  );
}

function Tag({ children }) {
  return (
    <span className="inline-flex items-center gap-1.5 bg-white/70 backdrop-blur border border-gray-200 rounded-full px-4 py-1.5 text-xs font-semibold text-navy">
      <span className="w-1.5 h-1.5 rounded-full bg-teal-500" />
      {children}
    </span>
  );
}

function FormErrorBanner({ errors }) {
  const count = Object.keys(errors).length;
  if (count === 0) return null;
  return (
    <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3 mb-6">
      <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
      <div>
        <div className="text-red-900 font-semibold">Please complete the highlighted fields below.</div>
        <div className="text-red-700 text-sm mt-1">{count} field{count > 1 ? "s" : ""} need{count > 1 ? "" : "s"} your attention.</div>
      </div>
    </div>
  );
}

function SuccessCard() {
  return (
    <div data-testid="driver-application-success" className="bg-white rounded-3xl p-10 md:p-14 border border-teal-100 text-center shadow-xl">
      <div className="w-16 h-16 rounded-full bg-teal-500/10 flex items-center justify-center mx-auto">
        <CheckCircle2 className="w-8 h-8 text-teal-600" />
      </div>
      <h3 className="font-heading text-3xl font-semibold text-navy mt-6">Application Received</h3>
      <p className="text-navy/70 mt-3 max-w-md mx-auto text-lg">
        Thank you for applying to Comfort Care Transportation. Our hiring team will review your application
        and reach out within 5 business days if it&apos;s a fit.
      </p>
      <Link to="/" className="btn-outline mt-8 inline-flex">Back to Home</Link>
    </div>
  );
}

function inputCls(error) {
  return `h-12 text-base ${error ? "border-red-400 focus-visible:ring-red-400 bg-red-50/40" : "border-gray-200 focus-visible:ring-teal-500"}`;
}
